// File: src/pages/AdminPage.jsx
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Grid, Card, CardContent, Typography, TextField, InputAdornment,
  IconButton, Chip, Button, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, Paper, Tooltip, Divider, Tabs, Tab
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DoDisturbIcon from '@mui/icons-material/DoDisturb';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import KeyIcon from '@mui/icons-material/Key';

import { AuthContext } from '../context/AuthContext';
import { createAuthenticatedAPI } from '../utils/apiClient';
import AdminFeedbackSection from './AdminFeedbackSection';

// Lazy-load chart library only on this page to keep bundle lean
const StatsChart = ({ last7, last30 }) => {
  // Simple inline bars without external libs for minimal footprint
  const max = Math.max(last7 || 0, last30 || 0, 1);
  const bar = (value, label, color) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ width: 140, color: 'text.secondary' }}>{label}</Box>
      <Box sx={{ flex: 1, background: 'rgba(0,0,0,0.06)', borderRadius: 1, height: 10 }}>
        <Box sx={{ width: `${(value / max) * 100}%`, height: '100%', borderRadius: 1, background: color }} />
      </Box>
      <Typography variant="body2" sx={{ width: 48, textAlign: 'right' }}>{value}</Typography>
    </Box>
  );
  return (
    <Box sx={{ display: 'grid', gap: 1.5 }}>
      {bar(last7, 'New users (7d)', 'linear-gradient(90deg, #3b82f6, #60a5fa)')}
      {bar(last30, 'New users (30d)', 'linear-gradient(90deg, #10b981, #34d399)')}
    </Box>
  );
};

const AdminPage = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const api = useMemo(() => createAuthenticatedAPI({ user, logout, navigate }), [user, logout, navigate]);

  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/');
      return;
    }
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const refreshAll = async () => {
    setLoading(true);
    try {
      const [s, u] = await Promise.all([
        api.get('/admin/stats'),
        api.get(`/admin/users?search=${encodeURIComponent(search)}${activeTab === 'active' ? '&active=true' : activeTab === 'inactive' ? '&active=false' : ''}`)
      ]);
      setStats(s);
      setUsers(u);
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = async (row) => {
    const path = row.is_active ? `/admin/users/${row.id}/deactivate` : `/admin/users/${row.id}/reactivate`;
    await api.post(path, {});
    await refreshAll();
  };

  const issueResetToken = async (row) => {
    const res = await api.post(`/admin/users/${row.id}/reset-password-token`, {});
    const token = res.reset_token;
    try {
      await navigator.clipboard.writeText(token);
      alert('Reset token copied to clipboard');
    } catch {
      window.prompt('Copy reset token:', token);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(180deg, #f8fafc, #eef2f7)' }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 800, background: 'linear-gradient(135deg, #1e40af, #3b82f6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Admin Dashboard
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button startIcon={<RefreshIcon />} onClick={refreshAll} disabled={loading}>
              Refresh
            </Button>
          </Box>
        </Box>

        {/* KPI Cards */}
        <Grid container spacing={3} sx={{ mb: 2 }}>
          {[
            { label: 'Total Users', value: stats?.total_users ?? '-', color: '#3b82f6' },
            { label: 'Active Users', value: stats?.active_users ?? '-', color: '#10b981' },
            { label: 'Admins', value: stats?.admin_users ?? '-', color: '#f59e0b' },
          ].map((kpi) => (
            <Grid item xs={12} md={4} key={kpi.label}>
              <Card sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">{kpi.label}</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: kpi.color }}>{kpi.value}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Growth Chart */}
        <Card sx={{ borderRadius: 3, mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>User Growth</Typography>
            <StatsChart last7={stats?.new_last_7 || 0} last30={stats?.new_last_30 || 0} />
          </CardContent>
        </Card>

        {/* Users Section */}
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
              <TextField
                placeholder="Search by name or email"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{ startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )}}
                sx={{ minWidth: 320 }}
              />
              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                textColor="primary"
                indicatorColor="primary"
              >
                <Tab value="all" label="All" />
                <Tab value="active" label="Active" />
                <Tab value="inactive" label="Inactive" />
              </Tabs>
              <Button variant="contained" onClick={refreshAll} disabled={loading}>Apply</Button>
            </Box>

            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Slug</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.id}</TableCell>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.email}</TableCell>
                      <TableCell>{row.public_slug}</TableCell>
                      <TableCell>{new Date(row.created_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Chip size="small" label={row.is_active ? 'Active' : 'Inactive'} color={row.is_active ? 'success' : 'default'} />
                        {row.is_admin && <Chip size="small" label="Admin" sx={{ ml: 1 }} />}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View public profile">
                          <IconButton onClick={() => window.open(`/u/${row.public_slug || row.email.replace('@','-').replaceAll('.','-')}`, '_blank')}>
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={row.is_active ? 'Deactivate' : 'Reactivate'}>
                          <IconButton onClick={() => toggleActive(row)}>
                            {row.is_active ? <DoDisturbIcon /> : <CheckCircleIcon />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Issue reset token">
                          <IconButton onClick={() => issueResetToken(row)}>
                            <KeyIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Feedback Section */}
        <Box sx={{ mt: 3 }}>
          <AdminFeedbackSection />
        </Box>
      </Container>
    </Box>
  );
};

export default AdminPage;


